/**
 * © 2025 Little Shilling, Inc. All rights reserved.
 * Shon Little
 * Created: 2024-01-16
 * Updated: 2025-07-24
 */

// Add third-party dependencies.
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  useTheme,
  Box,
  Typography,
  Paper,
  Button,
  Container,
} from '@mui/material';

/**
 * NotFound component for displaying 404 error pages.
 * @example
 * return (
 *   <NotFound />
 * )
 * @returns {React.ReactElement} component.
 */
const NotFound: React.FC = (): React.ReactElement => {
  // Add theme hooks.
  const theme = useTheme();

  // Set styles with proper theme integration.
  const styles = {
    text: {
      fontSize: '2rem',
      textAnchor: 'middle' as const,
      fill: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
    },
    leftCircle: {
      fill: theme.palette.primary.main,
      opacity: 0.7,
    },
    rightCircle: {
      fill: theme.palette.secondary.main,
      opacity: 0.7,
    },
  };

  // Render component.
  return (
    <Container maxWidth="lg" sx={{ py: 8, minHeight: '100vh' }}>
      <Box sx={{ textAlign: 'center' }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '3rem', md: '4rem' },
              color: 'primary.main',
              mb: 2,
            }}
          >
            Error 404
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.5rem', md: '2rem' },
              color: 'text.secondary',
              mb: 4,
            }}
          >
            Page Not Found
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
          >
            Oops! The page you&apos;re looking for seems to have wandered off.
            Let&apos;s get you back to where the magic happens.
          </Typography>
        </Box>

        {/* Action Button */}
        <Box sx={{ mb: 6 }}>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/"
            size="large"
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.2rem',
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Take Me Home
          </Button>
        </Box>

        {/* Venn Diagram Section */}
        <Paper
          sx={{
            p: 4,
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: 2,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              component="h3"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 2,
              }}
            >
              The Venn Diagram of Web Browsing
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Venn diagrams were introduced in 1880 by John Venn in a paper
              entitled &ldquo;On the Diagrammatic and Mechanical Representation
              of Propositions and Reasonings&rdquo; in the Philosophical
              Magazine and Journal of Science, about the different ways to
              represent propositions by diagrams.
            </Typography>
          </Box>

          {/* SVG Venn Diagram */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              component="svg"
              sx={{
                width: { xs: 350, sm: 500, md: 600 },
                height: { xs: 250, sm: 350, md: 400 },
                maxWidth: '100%',
              }}
              viewBox="0 0 600 400"
            >
              {/* Left Circle */}
              <Box
                component="circle"
                cx="200"
                cy="200"
                r="150"
                sx={styles.leftCircle}
              />
              {/* Right Circle */}
              <Box
                component="circle"
                cx="400"
                cy="200"
                r="150"
                sx={styles.rightCircle}
              />

              {/* Left Circle Text */}
              <Box
                component="text"
                x="155"
                y="200"
                sx={styles.text}
                textAnchor="middle"
              >
                Something
              </Box>
              <Box
                component="text"
                x="155"
                y="230"
                sx={styles.text}
                textAnchor="middle"
              >
                Broke
              </Box>

              {/* Intersection Text */}
              <Box
                component="text"
                x="300"
                y="200"
                sx={styles.text}
                textAnchor="middle"
              >
                404
              </Box>
              <Box
                component="text"
                x="300"
                y="230"
                sx={styles.text}
                textAnchor="middle"
              >
                Page
              </Box>

              {/* Right Circle Text */}
              <Box
                component="text"
                x="440"
                y="200"
                sx={styles.text}
                textAnchor="middle"
              >
                You Can&apos;t
              </Box>
              <Box
                component="text"
                x="440"
                y="230"
                sx={styles.text}
                textAnchor="middle"
              >
                Type
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Footer Message */}
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            &ldquo;The intersection of technical difficulties and user
            error&rdquo;
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

// Export component.
export default NotFound;
